/**
 * Chunk load recovery — max 2 automatic retries (second cache-busted), then honest error UI.
 * Prevents infinite ?b= reload loops when a lazy chunk 404s or returns HTML-as-JS.
 */
import { cacheBustedReload } from './bootRecovery';
import { diagnosticLog } from './diagnosticLogger';

export const CHUNK_RETRY_WINDOW_KEY = 'maestro_chunk_retry_window';
export const CHUNK_LOAD_MAX_AUTO_RETRIES = 2;

export type ChunkRetryWindow = { at: number; count: number };

export function readChunkRetryWindow(
  storage: Pick<Storage, 'getItem'>,
): ChunkRetryWindow | null {
  try {
    const raw = storage.getItem(CHUNK_RETRY_WINDOW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ChunkRetryWindow;
    if (!parsed || typeof parsed.at !== 'number' || typeof parsed.count !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** True when another automatic chunk retry is allowed (max 2 per page session). */
export function chunkRetryBudgetRemaining(
  storage: Pick<Storage, 'getItem'>,
): boolean {
  const window = readChunkRetryWindow(storage);
  if (!window) return true;
  return window.count < CHUNK_LOAD_MAX_AUTO_RETRIES;
}

export function consumeChunkRetryBudget(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now = Date.now(),
): number {
  let window = readChunkRetryWindow(storage);
  if (!window) {
    window = { at: now, count: 0 };
  }
  window.count += 1;
  window.at = now;
  storage.setItem(CHUNK_RETRY_WINDOW_KEY, JSON.stringify(window));
  return window.count;
}

export function resetChunkRetryBudget(storage: Pick<Storage, 'removeItem'>): void {
  try {
    storage.removeItem(CHUNK_RETRY_WINDOW_KEY);
  } catch {
    /* ignore */
  }
}

export function emitChunkLoadRetryBeacon(failedUrl: string, retryNumber: number): void {
  try {
    diagnosticLog('warn', 'js:render', 'chunk_load_retry', {
      failed_url: failedUrl,
      retry_number: retryNumber,
    });
  } catch {
    /* never propagate */
  }
}

export function emitChunkLoadFailedBeacon(
  failedUrl: string,
  route: string,
  retriesAttempted: number,
): void {
  try {
    diagnosticLog('error', 'js:render', 'chunk_load_failed', {
      failed_url: failedUrl,
      route,
      retries_attempted: retriesAttempted,
    });
  } catch {
    /* never propagate */
  }
}

/** Same-origin Vite hashed asset (entry or lazy chunk). */
export function isSameOriginHashedAssetUrl(url: string, pageHref: string): boolean {
  if (!url || /^ipc:/i.test(url)) return false;
  try {
    const u = new URL(url, pageHref);
    if (u.protocol === 'ipc:') return false;
    if (u.origin !== new URL(pageHref).origin) return false;
    return /\/assets\/[A-Za-z0-9_.-]+-[A-Za-z0-9_-]+\.(js|css)$/.test(u.pathname);
  } catch {
    return false;
  }
}

export function isDynamicChunkLoadFailure(reason: unknown): string | null {
  const msg = String(
    (reason && typeof reason === 'object' && 'message' in reason
      ? (reason as { message?: string }).message
      : reason) ?? '',
  );
  if (!msg) return null;
  const patterns = [
    /Failed to fetch dynamically imported module:\s*(.+)$/i,
    /Importing a module script failed/i,
    /Loading chunk [\d]+ failed/i,
    /error loading dynamically imported module/i,
  ];
  if (!patterns.some((p) => p.test(msg))) return null;
  const urlMatch = msg.match(/https?:[^\s'"]+/);
  return urlMatch?.[0] ?? msg;
}

export function currentRoutePath(win: Pick<Window, 'location'> = window): string {
  try {
    return win.location.pathname || '/';
  } catch {
    return '/';
  }
}

export function tryChunkLoadRetry(
  failedUrl: string,
  storage: Storage = sessionStorage,
  reloadFn: (href?: string) => void = cacheBustedReload,
): boolean {
  if (!chunkRetryBudgetRemaining(storage)) return false;
  const retryNumber = consumeChunkRetryBudget(storage);
  emitChunkLoadRetryBeacon(failedUrl, retryNumber);
  if (retryNumber >= CHUNK_LOAD_MAX_AUTO_RETRIES) {
    reloadFn();
  } else {
    try {
      window.location.reload();
    } catch {
      reloadFn();
    }
  }
  return true;
}

export function handleChunkLoadFailure(
  failedUrl: string,
  storage: Storage = sessionStorage,
  reloadFn: (href?: string) => void = cacheBustedReload,
  route = currentRoutePath(),
): 'retrying' | 'exhausted' {
  if (tryChunkLoadRetry(failedUrl, storage, reloadFn)) {
    return 'retrying';
  }
  const window = readChunkRetryWindow(storage);
  emitChunkLoadFailedBeacon(failedUrl, route, window?.count ?? CHUNK_LOAD_MAX_AUTO_RETRIES);
  return 'exhausted';
}

export function installChunkLoadRetryHooks(
  win: Window = window,
  doc: Document = document,
): void {
  win.addEventListener(
    'error',
    (ev) => {
      const target = ev.target as { tagName?: string; src?: string } | null;
      if (!target || target.tagName !== 'SCRIPT') return;
      const src = target.src || '';
      if (!isSameOriginHashedAssetUrl(src, win.location.href)) return;
      if (handleChunkLoadFailure(src, win.sessionStorage) === 'retrying') {
        ev.preventDefault?.();
      }
    },
    true,
  );

  win.addEventListener('unhandledrejection', (ev) => {
    const failed = isDynamicChunkLoadFailure(ev.reason);
    if (!failed) return;
    if (handleChunkLoadFailure(failed, win.sessionStorage) === 'retrying') {
      ev.preventDefault?.();
    }
  });
}
